import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLayout } from '../../context/LayoutContext';
import {
  HomeIcon,
  ChartBarIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  // BriefcaseIcon, // Desabilitado - menu Comercial
  FolderIcon,
  CurrencyDollarIcon,
  // CubeIcon, // Desabilitado - menu Estoque
} from '@heroicons/react/24/outline';

interface SubMenuItem {
  name: string;
  href: string;
}

interface MenuItem {
  name: string;
  href?: string;
  icon: React.ComponentType<any>;
  badge?: string;
  submenu?: SubMenuItem[];
}

const menuItems: MenuItem[] = [
  {
    name: 'Dashboard',
    href: '/',
    icon: HomeIcon,
  },
  // Menu Comercial desabilitado para homologação
  // {
  //   name: 'Comercial',
  //   icon: BriefcaseIcon,
  //   submenu: [
  //     { name: 'Clientes', href: '/clients' },
  //     { name: 'Propostas', href: '/proposals' },
  //     { name: 'Contratos', href: '/contracts' },
  //   ],
  // },
  {
    name: 'Cadastros',
    icon: FolderIcon,
    submenu: [
      // { name: 'Catálogo', href: '/catalog' }, // Desabilitado para homologação
      { name: 'Depósitos', href: '/warehouses' },
      { name: 'Feriados', href: '/holidays' },
    ],
  },
  // Menu Estoque desabilitado para homologação
  // {
  //   name: 'Estoque',
  //   icon: CubeIcon,
  //   submenu: [
  //     { name: 'Dashboard', href: '/estoque/dashboard' },
  //     { name: 'Materiais', href: '/estoque/materiais' },
  //     { name: 'Movimentações', href: '/estoque/movimentacoes' },
  //     { name: 'Notas Fiscais', href: '/estoque/notas-fiscais' },
  //   ],
  // },
  {
    name: 'Relatórios',
    href: '/reports',
    icon: ChartBarIcon,
  },
  {
    name: 'Financeiro',
    icon: CurrencyDollarIcon,
    submenu: [
      { name: 'Dashboard', href: '/financeiro/dashboard' },
      { name: 'Contas a Pagar', href: '/financeiro/contas-pagar' },
      { name: 'Contas a Receber', href: '/financeiro/contas-receber' },
      { name: 'Contas Bancárias', href: '/financeiro/contas' },
    ],
  },
  {
    name: 'Administração',
    icon: CogIcon,
    submenu: [
      { name: 'Usuários', href: '/users' },
      { name: 'Configurações', href: '/settings' },
      // { name: 'APIs', href: '/settings/apis' }, // Desabilitado para homologação
    ],
  },
];

const Sidebar: React.FC = () => {
  const { sidebarOpen, setSidebarOpen, isMobile } = useLayout();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(() => {
    // Expandir automaticamente o menu que contém a rota ativa
    const activeMenu = menuItems.find(item =>
      item.submenu?.some(sub => location.pathname.startsWith(sub.href))
    );
    return activeMenu ? [activeMenu.name] : [];
  });

  const handleLinkClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuName)
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    );
  };

  const isMenuExpanded = (menuName: string) => expandedMenus.includes(menuName);

  const isSubmenuActive = (submenu?: SubMenuItem[]) => {
    return submenu?.some(sub => location.pathname.startsWith(sub.href));
  };

  return (
    <aside className={`
      fixed left-0 top-16 bottom-0 w-56 bg-white border-r border-secondary-200 transition-transform duration-300 ease-in-out z-50
      ${isMobile ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : 'lg:translate-x-0'}
      shadow-lg
    `}>
      <nav className="h-full overflow-y-auto p-3 space-y-0 scrollbar-thin scrollbar-thumb-secondary-300 scrollbar-track-transparent pb-20">
        {menuItems.map((item) => (
          <div key={item.name}>
            {/* Menu principal (com ou sem submenu) */}
            {item.href ? (
              // Link direto (sem submenu)
              <NavLink
                to={item.href}
                onClick={handleLinkClick}
                className={({ isActive }) =>
                  `flex items-center px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700 border-l-2 border-primary-600 pl-2'
                      : 'text-secondary-700 hover:bg-secondary-50 hover:text-secondary-900'
                  }`
                }
              >
                <item.icon className="h-4 w-4 mr-1.5.5 flex-shrink-0" />
                <span className="truncate text-sm">{item.name}</span>
                {item.badge && (
                  <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 flex-shrink-0">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ) : (
              // Menu com submenu (expansível)
              <>
                <button
                  onClick={() => toggleMenu(item.name)}
                  className={`w-full flex items-center px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    isSubmenuActive(item.submenu)
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-secondary-700 hover:bg-secondary-50 hover:text-secondary-900'
                  }`}
                >
                  <item.icon className="h-4 w-4 mr-1.5.5 flex-shrink-0" />
                  <span className="truncate flex-1 text-left text-sm">{item.name}</span>
                  {isMenuExpanded(item.name) ? (
                    <ChevronDownIcon className="h-3.5 w-3.5 ml-auto flex-shrink-0" />
                  ) : (
                    <ChevronRightIcon className="h-3.5 w-3.5 ml-auto flex-shrink-0" />
                  )}
                </button>

                {/* Submenu expansível */}
                {isMenuExpanded(item.name) && item.submenu && (
                  <div className="mt-0.5 ml-3 space-y-0 pl-3 border-l-2 border-secondary-200 mb-0.5">
                    {item.submenu.map((subItem) => (
                      <NavLink
                        key={subItem.name}
                        to={subItem.href}
                        onClick={handleLinkClick}
                        className={({ isActive }) =>
                          `flex items-center px-2 py-1 text-xs rounded-md transition-colors ${
                            isActive
                              ? 'bg-primary-100 text-primary-700 font-medium border-l-2 border-primary-600 pl-1.5'
                              : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                          }`
                        }
                      >
                        <span className="truncate">{subItem.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;